import { defineStore } from 'pinia';
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import { useUIStore } from './ui.store';
import { useTransactionStore } from './transaction.store';
import { useConfigStore } from './config.store';
import { SubscriptionStatus, userRoles, type IUser, type Subscription } from '../helpers/types';
import router from '../router';
import { sleep } from '../utils/sleep';
import { setSentryUser } from '../helpers/sentry';

const baseUrl = `/api`;

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    userRole: null as null | userRoles,
    token: localStorage.getItem('token') as string | null,
    returnUrl: null as null | string,
    user: null as IUser | null | undefined,
  }),
  getters: {
    isAuth: (store) => !!store.user,
    isStoreOwner: (store) => store.user?.role == userRoles.DEFAULT_USER,
    isAdmin: (store) => store.user?.role == userRoles.ADMIN,
  },
  actions: {
    async auth(
      preventRedirectToLoginWhenFailedAuth?: boolean
    ) {
      const UI = useUIStore();
      const token = localStorage.getItem('token');
      this.token = token;
      if (token) {
        UI.startLoading();
        return fetchWrapper.get(`${baseUrl}/me`)
          .then(async (data) => {
            this.user = data?.user;
            setSentryUser(data?.user);
            const transaction = useTransactionStore();
            const configStore = useConfigStore();
            await transaction.getMyCredits();
            await transaction.fetchTransactions();
            if (!configStore.configLoaded && this.user?.role != userRoles.SUBUSER) {
              await configStore.getMyConfig();
            }
          })
          .catch((err) => {
            console.error(err);
            UI.showMessage("error", err?.response?.data?.message ?? err?.message ?? "Erro ao obter credenciais");
            this.logout(preventRedirectToLoginWhenFailedAuth);
          })
          .finally(() => {
            UI.stopLoading();
          });
      } else {
        localStorage.removeItem('token');
      }
    },
    async handleLoginResponseData(user: any, token: any) {
      console.log('handleLoginResponseData', { user, token })
      this.token = token;
      this.userRole = user.role;
      localStorage.setItem('token', token);
      await sleep(1000);
      await this.auth();  // Aguarda a autenticação antes de prosseguir

      // Atualiza o estado do usuário antes de redirecionar
      this.user = user;
    },
    async login(email: string, password: string): Promise<IUser> {
      try {
        const { user, token } = await fetchWrapper.post(`${baseUrl}/login`, { email, password });
        await this.handleLoginResponseData(user, token);
        return user;
      } catch (error: any) {
        console.error('Login error:', error, typeof error);
        throw new Error(error?.data?.message ?? error?.data?.error ?? JSON.stringify(error));
      }
    },
    async handlePostLoginActions(role: userRoles, currentSubscription?: Subscription) {
      console.log({ role })
      const route = router.currentRoute.value;
      if (
        currentSubscription?.status == SubscriptionStatus.INACTIVE &&
        currentSubscription?.url &&
        [userRoles.DEFAULT_USER, userRoles.SUBUSER].includes(Number(role))
      ) {
        router.push(route?.query?.from ? decodeURIComponent(route?.query?.from?.toString()) : "/u/subscription");
        window.open(currentSubscription?.url, '_blank');
      } else {
        switch (Number(role)) {
          case userRoles.ADMIN:
            router.push(route?.query?.from ? decodeURIComponent(route?.query?.from?.toString()) : { name: 'priceTables' });
            break;
          default:
            router.push(route?.query?.from ? decodeURIComponent(route?.query?.from?.toString()) : "/u/home");
            break;
        }
      }
    },
    async recoverPassword(email: string) {
      const UI = useUIStore();
      try {
        UI.startLoading();
        const res = await fetchWrapper.post(`${baseUrl}/recover-password`, { email });

        return res;
      } catch (error: any) {
        console.error('Login error:', error, typeof error);
        throw new Error(error?.data?.message ?? error?.data?.error ?? JSON.stringify(error));
      } finally {
        UI.stopLoading();
      }
    },
    logout(
      preventRedirectToLoginWhenFailedAuth?: boolean
    ) {
      setSentryUser(null);
      this.user = null;
      this.token = null;
      localStorage.removeItem('token');
      if (!preventRedirectToLoginWhenFailedAuth) {
        router.push('/login');
      }
    },
  }
});
